import * as React from 'react';

import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';

import { SingularDeletionPrompt } from './components/SingularDeletionPrompt';
import { BulkDeletionPrompt } from './components/BulkDeletionPrompt';
import { DeletionHeader } from './components/DeletionHeader';
import { ConfirmDeletionButton } from './components/ConfirmDeletionButton';

import { genericDeletableItemDisplayText } from './constants';
import { DeletableItem, DeletableItemDisplayText } from './types';

/** Props to be provided to the `<DeleteItemsModal />` component */
export type DeleteItemsModalProps = {
  /** Click handler when the modal is closed by either the "Cancel" button or by the modal component */
  onClose: () => void;

  /** Click handler when the user clicks the "Confirm" button to delete item */
  onConfirm: () => void;

  /** Array of content items being deleted */
  content?: DeletableItem[];

  /** Array of folder items being deleted */
  folders?: DeletableItem[];

  /** Object containing the text to be rendered in the delete modal */
  displayTextForContentItems?: DeletableItemDisplayText;

  /** Node containing a warning message related to the single/bulk deletion */
  warning?: React.ReactNode;
};

/**
 * Modal component displayed on index pages when the user is deleting items. Use
 * this modal to confirm with the user that they actually intend to delete certain
 * items.
 */
export const DeleteItemsModal = ({
  onClose,
  onConfirm,
  content = [],
  folders = [],
  displayTextForContentItems = genericDeletableItemDisplayText,
  warning,
  ...props
}: DeleteItemsModalProps) => {
  if (folders.length + content.length === 0) {
    return null;
  }

  return (
    <Modal
      data-test="delete-items-modal-wrapper"
      id="delete-items-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
      {...props}
    >
      <ModalHeader data-test="delete-items-modal-header">
        <DeletionHeader
          displayTextForContentItems={displayTextForContentItems}
          folders={folders}
          content={content}
        />
      </ModalHeader>
      <ModalBody data-test="delete-items-modal-body" id="delete-items-modal-body">
        {folders.length + content.length > 1 ? (
          <BulkDeletionPrompt
            folders={folders}
            content={content}
            displayTextForContentItems={displayTextForContentItems}
            warning={warning}
          />
        ) : (
          <SingularDeletionPrompt
            folders={folders}
            content={content}
            displayTextForContentItems={displayTextForContentItems}
            warning={warning}
          />
        )}
      </ModalBody>
      <ModalFooter data-test="delete-items-modal-actions" id="delete-items-modal-actions">
        <ConfirmDeletionButton
          folders={folders}
          content={content}
          onClick={onConfirm}
          displayTextForContentItems={displayTextForContentItems}
          data-test="delete-items-modal-confirm-button"
        />
      </ModalFooter>
    </Modal>
  );
};
